const { debounce, getOffsetTop } = window.BAO.Utils
const {
  depictProductCard,
  depictFilters,
  formatDepictFilters,
  formatDepictProduct,
} = window.BAO.Depict

class DepictSearch extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.query = ''

    this.searchProducts = []
    this.contentSearchResults = []

    this.cursor = null
    this.offset = 0
    this.allProductsLoaded = false

    this.filters = []
    this.selectedFilters = []
    this.expandedFilters = []

    this.isTicking = false
    this.lastKnownY = 0

    this.isLoadingPagination = false

    const urlParams = new URLSearchParams(window.location.search)
    this.query = urlParams.get('q')

    this.requestTick = this.requestTick.bind(this)
    this.updateEl = this.updateEl.bind(this)

    if (!this.query || this.searchFormat === 'drawer') return

    this.getSearchResults()
  }

  setupListeners () {
    super.setupListeners()

    if (this.searchFormat === 'drawer') {
      this.onInputChange = this.onInputChange.bind(this)
      this.debouncedOnInputChange = debounce((event) => {
        this.onInputChange(event)
      }, 500)

      this.listeners.add(
        this.els.input.element,
        'input',
        this.debouncedOnInputChange,
      )
    }

    this.onFilterChange = this.onFilterChange.bind(this)
    this.listeners.add(this, 'change', this.onFilterChange.bind(this))

    this.handleMoreLessToggle = this.handleMoreLessToggle.bind(this)
    this.listeners.add(this, 'click', this.handleMoreLessToggle.bind(this))

    this.resetAllFilters = this.resetAllFilters.bind(this)
    this.listeners.add(
      this.els['filters-clear'].element,
      'click',
      this.resetAllFilters.bind(this),
    )

    this.onScroll = this.onScroll.bind(this)
    if (this.searchFormat === 'drawer') {
      this.els['products-panel'].element.addEventListener(
        'scroll',
        this.onScroll,
        { passive: true },
      )
    } else {
      window.addEventListener('scroll', this.onScroll, { passive: true })
    }
  }

  onInputChange () {
    this.query = this.els.input.element.value.trim()
    this.cursor = null
    this.offset = 0

    this.getSearchResults()
  }

  async getSearchResults (action = 'default') {
    if (!this.query.length || this.query.length < 3) {
      this.resetSearch()

      return
    }

    // Generate or retrieve session_id
    let sessionId = sessionStorage.getItem('session_id')
    if (!sessionId) {
      sessionId = self.crypto.randomUUID()
      sessionStorage.setItem('session_id', sessionId)
    }

    if (action === 'default') {
      this.cursor = null
      this.offset = 0
      this.allProductsLoaded = false
      this.isLoadingPagination = false
    }

    try {
      const url = `https://api.depict.ai/v3/search/results`

      // Added top variables to stop line size stopping commits
      const petiteMarkets = window.theme.depict.petiteMarkets
      const marketHandle = window.theme.depict.marketHandle
      const isPetiteRegion = petiteMarkets.includes(marketHandle)
      const petiteFilter =
        window.theme.depict.showPetite && isPetiteRegion
          ? ['true', 'false']
          : ['false']

      const filters = [
        ...this.selectedFilters.map((filter) => ({
          field: filter.key,
          data: filter.values,
          op: filter.op,
        })),
        {
          field: 'app_exclusive',
          data: false,
          op: 'eq',
        },
        {
          field: 'is_petite',
          op: 'in',
          data: petiteFilter,
        },
      ]

      const queryObject = {
        merchant: window.theme.depict.merchant,
        market: window.theme.depict.country.toLowerCase(),
        locale: window.theme.depict.language,
        limit: this.limit,
        cursor: this.cursor,
        query: this.query,
        filters,
        session_id: sessionId,
      }

      const [productResults, suggestionResults] = await Promise.all([
        fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(queryObject),
        }).then((response) => response.json()),
        fetch(
          `https://api.depict.ai/v3/search/suggestions?merchant=${window.theme.depict.merchant}&market=${window.theme.depict.country.toLowerCase()}&locale=${window.theme.depict.language}&query=${this.query}`,
        ).then((response) => response.json()),
      ])

      const data = {
        productResults,
        suggestionResults,
      }

      this.handleSearchResults({ data, action })
    } catch (error) {
      throw new Error(error)
    }
  }

  async handleSearchResults (response) {
    const { data, action } = response

    const products = data?.productResults.displays

    this.filters = formatDepictFilters(
      data?.productResults.filters,
      this.selectedFilters,
    )

    if (action === 'offset') {
      // Append new product results to existing array
      this.searchProducts = [...this.searchProducts, ...products]
    } else {
      this.searchProducts = products
    }

    this.contentSearchResults = data.content_search_links
    this.cursor = data.cursor

    const filtersHTML = depictFilters({
      filters: this.filters,
      expandedFilters: this.expandedFilters,
      format: 'search',
    })
    this.els.filters.element.innerHTML = filtersHTML

    const productCardsHTML = this.searchProducts
      ? await this.getAllProductCardHTML()
      : ''

    if (action === 'default') {
      this.els['product-grid'].element.innerHTML = ''

      if (this.searchFormat === 'drawer') {
        const productPanelEl = this.querySelector(
          '[data-depict-search-el="products-panel"]',
        )
        productPanelEl.scrollTo({ top: 0, behavior: 'instant' })
      }
    }

    this.els['product-grid'].element.innerHTML =
      productCardsHTML ||
      `<li class="${this.classPrefix}_Item ${this.classPrefix}_Item-empty"><p class="prd-List_Empty fz-14_150">${window.theme.depict.empty}</p></li>`

    if (this.searchFormat === 'drawer') {
      const suggestions = data.suggestionResults.suggestions.filter(
        (result) => result.type === 'query',
      )
      const suggestionsHTML = this.getSuggestionsHTML(suggestions)
      this.els.suggestions.element.innerHTML = suggestionsHTML

      const contentSuggestions = data.productResults.content_search_links
      const contentSuggestionsHTML =
        this.getContentSuggestionsHTML(contentSuggestions)
      this.els['content-suggestions'].element.innerHTML =
        contentSuggestionsHTML

      const collections = data.suggestionResults.suggestions.filter(
        (result) =>
          result.type === 'listing' && result.listing_type === 'category',
      )
      const collectionsHTML = this.getCollectionsHTML(collections)

      this.els.collections.element.innerHTML = collectionsHTML

      this.classList.toggle('drw-Search-showResults', true)

      this.els['default-panel'].element.setAttribute('aria-hidden', true)
      this.els['products-panel'].element.setAttribute('aria-hidden', false)
      this.els['suggestions-panel'].element.setAttribute('aria-hidden', false)

      this.els['clear-panel'].element.classList.toggle(
        'drw-Search_Buttons-hasFilters',
        this.selectedFilters.length > 0,
      )
      this.els['filters-clear'].element.setAttribute(
        'aria-hidden',
        this.selectedFilters.length === 0,
      )
      this.els['filters-close'].element.textContent =
        window.theme.strings.showCountItems.replace(
          '[count]',
          data?.productResults.n_hits,
        )
    } else {
      this.els['results-count'].element.textContent =
        window.theme.strings.showingCountItems.replace(
          '[count]',
          data?.productResults.n_hits,
        )
    }

    const fetchedProductCount = products?.length + this.offset
    this.allProductsLoaded = fetchedProductCount >= data?.productResults.n_hits

    this.offset = Number(fetchedProductCount)

    if (action === 'offset') {
      this.isLoadingPagination = false
    }
  }

  async getAllProductCardHTML () {
    const productCardHTML = await Promise.all(
      this.searchProducts.map(
        async (productData, index) =>
          await this.getProductCardHTML(productData, index),
      ),
    )
    return productCardHTML.filter(Boolean).join('')
  }

  async getProductCardHTML (productData, index) {
    const formattedProduct = formatDepictProduct(productData)

    // console.log(productData)
    // console.log(formattedProduct)

    return `
      <li class="${this.classPrefix}_Item" data-index="${index}" data-depict-search-el="item" style="--Order_Desktop: ${index + 1}; --Order_Mobile: ${index + 1};">
        ${depictProductCard({ formattedProduct, index, showFlatImages: false })}
      </li>`
  }

  resetSearch () {
    this.classList.toggle('drw-Search-showResults', false)

    this.els['default-panel'].element.setAttribute('aria-hidden', false)
    this.els['products-panel'].element.setAttribute('aria-hidden', true)
    this.els['suggestions-panel'].element.setAttribute('aria-hidden', true)
  }

  getSuggestionsHTML (suggestionsData) {
    const returnedSuggestions = suggestionsData.map((suggestion) => {
      return `<div class="drw-Search_LinkItem">
        <a
          href="${window.routes.searchUrl}?q=${encodeURI(suggestion.query)}"
          class="drw-Search_Link fz-14_150"
        >
          ${suggestion.query}
        </a>
      </div>`
    })

    return returnedSuggestions.filter(Boolean).join('')
  }

  getContentSuggestionsHTML (contentSuggestionsData) {
    const returnedContentSuggestions = contentSuggestionsData.map(
      (suggestion) => {
        const { title = '', highlights = [] } = suggestion

        const linkText = highlights.length > 0 ? highlights[0].snippet : title

        return `<div class="drw-Search_LinkItem">
        <a
          href="${suggestion.page_url}"
          class="drw-Search_Link fz-14_150"
        >
          ${linkText}
        </a>
      </div>`
      },
    )

    return returnedContentSuggestions.filter(Boolean).join('')
  }

  getCollectionsHTML (collectionsData) {
    const returnedCollections = collectionsData.map((collection) => {
      return `<div class="drw-Search_LinkItem">
        <a
          href="${window.routes.collectionsUrl}/${collection.slug}"
          class="drw-Search_Link fz-14_150"
        >
          ${collection.title}
        </a>
      </div>`
    })

    return returnedCollections.filter(Boolean).join('')
  }

  onFilterChange (event) {
    const changedFilterEl = event.target.closest(
      '[data-depict-search-el="checkbox"]',
    )

    if (!changedFilterEl) return

    const key = changedFilterEl.dataset.filterKey
    const value = changedFilterEl.dataset.filterOptionValue
    const op = changedFilterEl.dataset.filterOp

    // Check if filter object exists in selected filters
    const currentFilter = this.selectedFilters.find(
      (filter) => filter.key === key,
    )

    if (currentFilter) {
      // Check if checkbox is selected and the value doesn't already exist
      const isChecked = changedFilterEl.checked
      const valueExists = currentFilter.values.includes(value)

      if (isChecked && !valueExists) {
        currentFilter.values.push(value)
      } else if (!isChecked && valueExists) {
        const valueIndex = currentFilter.values.indexOf(value)
        currentFilter.values.splice(valueIndex, 1)

        // Delete filter object from array if no value is selected
        if (!currentFilter.values.length) {
          const filterIndex = this.selectedFilters.indexOf(currentFilter)
          this.selectedFilters.splice(filterIndex, 1)
        }
      }
    } else {
      // Create new filter object in selected filters
      this.selectedFilters.push({
        key,
        values: [value],
        op,
        settings: {
          singleSelect: false,
        },
      })
    }

    this.getSearchResults()
  }

  handleMoreLessToggle (event) {
    const clickedTriggerEl = event.target.closest(
      '[data-depict-search-el="more-toggle"]',
    )
    if (!clickedTriggerEl) return

    const matchedGroupEl = this.querySelector(
      `[data-depict-search-el="group"][data-key="${clickedTriggerEl.dataset.key}"]`,
    )

    const currentState = matchedGroupEl.classList.contains(
      'drw-Filters_Group-expanded',
    )

    matchedGroupEl.classList.toggle(
      'drw-Filters_Group-expanded',
      !currentState,
    )

    if (currentState) {
      const currentFilter = this.expandedFilters.find(
        (filterGroup) => filterGroup.key === clickedTriggerEl.dataset.key,
      )
      const filterIndex = this.expandedFilters.indexOf(currentFilter)
      this.expandedFilters.splice(filterIndex, 1)
    } else {
      this.expandedFilters.push({
        key: clickedTriggerEl.dataset.key,
      })
    }
  }

  resetAllFilters () {
    this.selectedFilters = []

    this.getSearchResults()
  }

  onScroll (event) {
    if (this.allProductsLoaded) return

    this.requestTick()
  }

  requestTick () {
    if (!this.isTicking) requestAnimationFrame(this.updateEl)

    this.isTicking = true
  }

  updateEl () {
    this.isTicking = false

    this.handleScroll()
  }

  handleScroll () {
    let triggerLoadMore = false

    if (this.searchFormat === 'page') {
      this.lastKnownY = window.scrollY

      const productGridEnd = getOffsetTop(this) + this.offsetHeight
      const loadMoreTriggerPoint = productGridEnd - window.innerHeight * 1.5

      triggerLoadMore = loadMoreTriggerPoint < this.lastKnownY
    } else {
      const productPanelEl = this.querySelector(
        '[data-depict-search-el="products-panel"]',
      )
      const productGridEl = this.querySelector(
        '[data-depict-search-el="product-grid"]',
      )

      const productPanel = productPanelEl.getBoundingClientRect()
      const productGrid = productGridEl.getBoundingClientRect()

      triggerLoadMore = productGrid.bottom - productPanel.bottom - 18 <= 0
    }

    if (triggerLoadMore && this.isLoadingPagination === false) {
      this.isLoadingPagination = true

      this.getSearchResults('offset')
    }
  }

  get limit () {
    return parseInt(this.dataset.limit, 10)
  }

  get searchFormat () {
    return this.dataset.format || 'drawer'
  }

  get classPrefix () {
    return this.searchFormat === 'page' ? 'prd-List' : 'drw-Search'
  }
}

if (!customElements.get('depict-search')) {
  customElements.define('depict-search', DepictSearch)
}
